import { Box, IconButton, Pagination, Stack, Typography, Chip, Divider } from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import CustomDataGrid2 from '@src/components/CustomDataGrid/CustomDataGrid2';
import { IsMobileWidth, IsTabletWidth } from '@src/components/MediaQueryUtils';
import PageHeader from '@src/components/PageHeader/PageHeader';
import SearchBar from '@src/components/SearchBar';
import { render } from '@testing-library/react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import ChatIcon from '@mui/icons-material/Chat';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import CustomAlert from '@src/components/CustomAlert/CustomAlert';
import { formatPriceValue, roundTo2Decimals } from '@src/helpers/Helpers';
import CustomDrawer from '@src/components/CustomDrawer/CustomDrawer';
import { ProductResponse } from '@src/interfaces/type';
import ViewOrder from '@src/components/OrderView/ViewOrder';
import { useAppSelector } from '@src/store/hooks';
import { AxiosError } from 'axios';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	transition: width 0.3s ease;
`;

const ExportButtonStyles = styled('div')({
	'@media (max-width: 1024px)': {
		marginLeft: '16px',
	},
	'@media (max-width: 478px)': {
		background: 'white',
		display: 'flex',
		marginLeft: '0px',
		position: 'fixed',
		bottom: 0,
		zIndex: '100',
		width: '100%',
		padding: '16px',
		left: 0,
		borderRadius: '6px 6px 0px 0px',
		borderTop: '1.5px solid #F1F1F1',
	},
});

const getColumns = (onChatPress: any, onSeeAllPress: any) => [
	{
		field: 'organization_name',
		headerName: 'Dealer',
		width: 200,
		renderCell: (params: any) => (
			<Stack direction="row" alignItems={'center'} gap={1}>
				<img
					src={params?.row?.image || '/Cheeni-fallback.svg'}
					alt="organization-logo"
					width="30px"
					height="30px"
					style={{ borderRadius: '50%' }}
				/>
				{params?.row?.name}
			</Stack>
		),
	},
	{
		field: 'name',
		headerName: 'Contact Name',
		width: 200,
		renderCell: (params: any) => (
			<div>{params?.row?.customers?.firstname + ' ' + params?.row?.customers?.lastname}</div>
		),
	},
	{
		field: 'contact_no',
		headerName: 'Contact No',
		width: 200,
		renderCell: (params: any) => <div>{params?.row?.customers?.telephone}</div>,
	},
	{
		field: 'credit_limit',
		headerName: 'Credit Limit',
		width: 200,
		renderCell: (params: any) => <div>Rs {params?.row?.restaurant_credit_limit?.credit_limit}</div>,
	},
	{
		field: 'pending',
		headerName: 'Pending',
		width: 200,
		renderCell: (params: any) => <div>Rs {params?.row?.restaurant_credit_limit?.pending}</div>,
	},
	{
		field: 'remaining',
		headerName: 'Remaining',
		width: 200,
		renderCell: (params: any) => {
			const pending = params?.row?.restaurant_credit_limit?.pending;
			const creditLimit = params?.row?.restaurant_credit_limit?.credit_limit;
			const remaining = creditLimit - pending;
			if (remaining < 0) {
				return <div style={{ color: 'red' }}>Over Limit</div>;
			}
			return <div>Rs {roundTo2Decimals(remaining)}</div>;
		},
	},
	{
		field: 'actions',
		headerName: 'Actions',
		width: 150,
		renderCell: (params: any) => {
			return (
				<Stack direction={'row'} gap={1} alignItems={'center'}>
					<Typography onClick={(e) => onSeeAllPress(e, params.row)} color={'primary'}>
						See Orders
					</Typography>
					<IconButton onClick={(e) => onChatPress(e, params.row)}>
						<ChatIcon />
					</IconButton>
				</Stack>
			);
		},
	},
];

const ordersColumns = [
	{
		field: 'timeId',
		headerName: 'Order ID',
		width: 200,
		// headerAlign: 'center',
		// align: 'center',
	},
	{ field: 'orderDate', headerName: 'Ordered', width: 230 },
	{ field: 'deliveryDate', headerName: 'Delivery Date', width: 200 },
	{
		field: 'statusData',
		headerName: 'Order Status',
		width: 168,
		renderCell: (params: any) => (
			<Chip
				label={params.row?.orderStatus?.name}
				style={{
					backgroundColor: params.row?.orderStatus?.color_bg,
					color: params?.row?.orderStatus?.color,
					fontWeight: 'bold',
					borderRadius: '4px',
				}}
			/>
		),
	},
	{
		field: 'paymentStatus',
		headerName: 'Payment Status',
		width: 168,
		renderCell: (params: any) => {
			if (!params.row?.paymentStatus?.name) {
				return null;
			}
			const bgMap: any = {
				Paid: {
					color: '#00BFA5',
					color_bg: '#E0F3F2',
				},
				'Partially Paid': {
					color: '#FFC107',
					color_bg: '#FFF3E0',
				},
				Unpaid: {
					color: '#F44336',
					color_bg: '#FFEBEE',
				},
				Overdue: {
					color: '#F44336',
					color_bg: '#FFEBEE',
				},
			};
			const name = params.row?.paymentStatus?.name;
			const color = bgMap[name]?.color;
			const color_bg = bgMap[name]?.color_bg;
			return (
				<Chip
					label={name}
					style={{
						backgroundColor: color_bg,
						color: color,
						fontWeight: 'bold',
						borderRadius: '4px',
					}}
				/>
			);
		},
	},
	{
		field: 'creditStatus',
		headerName: 'Credit Status',
		width: 168,
		renderCell: (params: any) => (
			<Chip
				label={params.row?.creditStatus.split('_').join(' ')}
				style={{
					// backgroundColor: params.row?.statusData?.color_bg,
					color: params?.row?.creditStatus === 'AVAILABLE' ? 'green' : 'red',
					fontWeight: 'bold',
					borderRadius: '4px',
				}}
			/>
		),
	},
];

function RenderOrderDetails({
	orderDetails,
	setOrderDetails,
	orderInfo,
	setOrderInfo,
	currentUser,
	alert,
	setAlert,
	displayCustomError,
	viewOrder,
	setViewOrder,
}: any) {
	const isMobileWidth = IsMobileWidth();
	if (orderDetails) {
		return (
			<CustomDrawer
				open={viewOrder}
				setOpen={setViewOrder}
				style={{ width: isMobileWidth ? '100%' : '80%' }}
				header={false}
			>
				<ViewOrder
					alert={alert}
					setAlert={setAlert}
					orderDetails={orderDetails}
					setOrderDetails={setOrderDetails}
					orderInfo={orderInfo}
					setOrderInfo={setOrderInfo}
					currentUser={currentUser}
					title={orderDetails.firstname}
					handleClose={() => setViewOrder(false)}
					displayCustomError={displayCustomError}
					// setApiCall={setApiCall}
					// isOrderPage={true}
				/>
			</CustomDrawer>
		);
	}
	return null;
}

function RenderCustomerOrders({
	showAllCustomerOrders,
	customerOrders,
	setShowAllCustomerOrders,
	currentCustomer,
	alert,
	setAlert,
	loadingOrders,
}: any) {
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);

	const [viewOrder, setViewOrder] = useState(false);
	const [orderDetails, setOrderDetails] = useState(null);
	const [orderInfo, setOrderInfo] = useState(null);

	const [errorMessage, setErrorMessage] = useState('');
	const [isErrorVisible, setErrorVisible] = useState(false);

	if (!showAllCustomerOrders) return null;

	const handleRowClick = (params: any) => {
		const updatedOrderProducts = params?.row?.products?.map((item: ProductResponse) => {
			return {
				...item,
				id: item?.productId,
				price: formatPriceValue(item?.price),
				quantity: item?.quantity,
				category: item?.category?.name,
				categoryId: item?.category?.id,
				subCategory: item?.subCategory?.name,
				subCategoryId: item?.subCategory?.id,
				maxPrice: formatPriceValue(item?.maxPrice),
				machineBrand: item?.machineBrand?.name,
				machineBrandId: item?.machineBrand?.id,
				machineModel: item?.machineModel?.name,
				machineModelId: item?.machineModel?.id,
				groupCategory: item?.groupCategory?.name,
				groupCategoryId: item?.groupCategory?.id,
				brand: item?.brand?.name,
				brandId: item?.brand?.id,
				partComposition: item?.partComposition?.name,
				partCompositionId: item?.partComposition?.id,
				partDesign: item?.partDesign?.name,
				partDesignId: item?.partDesign?.id,
				partDiameter: item?.partDiameter?.name,
				partDiameterId: item?.partDiameter?.id,
				partDimension: item?.partDimension?.name,
				partDimensionId: item?.partDimension?.id,
				partOwnership: item?.partOwnership?.name,
				partOwnershipId: item?.partOwnership?.id,
				partQuality: item?.partQuality?.name,
				partQualityId: item?.partQuality?.id,
				partWeight: item?.partWeight?.name,
				partWeightId: item?.partWeight?.id,
				partPerformance: item?.partPerformance?.name,
				partPerformanceId: item?.partPerformance?.id,
			};
		});
		setOrderDetails({
			...params?.row,
			orderDate: params?.row?.orderDate,
			requestedDeliveryDate: params?.row?.requestedDeliveryDate,
			products: updatedOrderProducts,
			orderStatus: {
				color: params?.row?.orderStatus?.color,
				color_bg: params?.row?.orderStatus?.color_bg,
				name: params?.row?.orderStatus?.name,
				id: params?.row?.orderStatus?.id,
				validation_order: params?.row?.orderStatus?.validation_order,
				group_id: params?.row?.orderStatus?.group_id,
			},
		});
		setViewOrder(true);
	};

	const displayCustomError = (error: AxiosError) => {
		const msg = error?.response?.data?.message || 'Something went wrong!';
		setErrorMessage(msg);
		setErrorVisible(true);
	};

	return (
		<>
			<CustomDrawer
				open={showAllCustomerOrders}
				style={{ width: '80%' }}
				setOpen={setShowAllCustomerOrders}
				header={false}
			>
				<Box m={1} sx={{ width: '100%', overflowX: 'auto' }}>
					<Typography sx={{ mt: 2, mb: 2 }} fontSize={20}>
						Customer Orders for {currentCustomer?.name}
					</Typography>
					<Divider />
					<CustomDataGrid2
						rows={customerOrders}
						columns={ordersColumns}
						checkboxSelection={false}
						style={{
							transition: 'height 0.3s ease',
							maxHeight: 'calc(100vh - 100px)',
							minHeight: 'calc(100vh - 100px)',
						}}
						// showNoRowsFound={!orders?.length}
						// onRowsSelectionHandler={onRowsSelectionHandler}
						handleRowClick={handleRowClick}
						// responsiveStyles={gridResponsiveStyles}
						loading={loadingOrders}
						type="server"
						filterType="server"
					/>
				</Box>
			</CustomDrawer>
			<RenderOrderDetails
				{...{
					orderDetails,
					setOrderDetails,
					orderInfo,
					setOrderInfo,
					currentUser,
					alert,
					setAlert,
					displayCustomError,
					viewOrder,
					setViewOrder,
				}}
			/>
		</>
	);
}

function CustomerList() {
	const { id } = useParams();
	const navigate = useNavigate();
	const isMobile = IsMobileWidth();
	const [searchValue, setSearchValue] = useState('');
	const [totalPages, setTotalPages] = useState(1);
	const [page, setPage] = useState(1);
	const [searching, setSearching] = useState(false);
	const [showAllCustomerOrders, setShowAllCustomerOrders] = useState(false);
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const [shareModal, setShareModal] = useState(false);
	const [loadingCustomerList, setLoadingCustomerList] = useState<boolean>(false);
	const [loadingOrders, setLoadingOrders] = useState<boolean>(false);
	const [customerList, setCustomerList] = useState([]);
	const { apiService }: any = React.useContext(ApplicationContext);
	const [customerOrders, setCustomerOrders] = useState([]);
	const [currentCustomer, setCurrentCustomer] = useState({});
	const [exporting, setExporting] = useState(false);
	const [alert, setAlert] = useState({
		message: '',
		severity: 'success',
	});
	const limit = 25;

	let params = {
		limit: limit,
		page: page,
		search: searchValue,
	};

	const onChatPress = (e: any, data: any) => {
		e.preventDefault();
		console.log(data);
		const chatId = data?.phone;
		navigate(`/suppliers/${id}/chat/${chatId}`);
	};

	const onSeeAllPress = (e: any, data: any) => {
		e.preventDefault();
		console.log(data);
		setCurrentCustomer(data);
		fetchCustomerOrders(data?.id);
		setShowAllCustomerOrders(true);
	};

	const columns = getColumns(onChatPress, onSeeAllPress);
	const handleSearch = (value: any) => {
		setSearching(true);
		setSearchValue(value);
		setPage(1);
	};

	const handlePageChange = (event: any, value: number) => {
		if (page !== value) {
			setPage(value);
			setSelectedItemIds([]);
			setLoadingCustomerList(true);
		}
	};

	const onRowsSelectionHandler = (ids: string[]) => {
		setSelectedItemIds(ids);
	};

	useEffect(() => {
		fetchCustomerList();
	}, [searchValue]);

	function fetchCustomerList() {
		setLoadingCustomerList(true);
		apiService.getCustomerList(
			(response: any) => {
				console.log(response);
				const data = response?.data?.data;
				const { totalCount, customers } = data || {};
				setCustomerList(customers);
				setTotalPages(Math.ceil(totalCount / limit));
				setLoadingCustomerList(false);
			},
			(error: any) => {
				console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
				setLoadingCustomerList(false);
			},
			id,
			params
		);
	}

	function fetchCustomerOrders(customerId: any) {
		setLoadingOrders(true);
		apiService.getCustomerOrders(
			(response: any) => {
				console.log(response);
				const data = response?.data?.data;
				const { orders } = data || {};
				const orderdata = orders.map((order: any) => ({ ...order, id: order.cheeniOrderId }));
				setCustomerOrders(orderdata);
				setLoadingOrders(false);
			},
			(error: any) => {
				console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
				setLoadingOrders(false);
			},
			customerId
		);
	}

	function exportCustomerList(customerId: any) {
		setExporting(true);
		apiService.exportCustomerList(
			(response: any) => {
				console.log(response);
				const report = response?.data?.data?.report;
				window.open(report, '_blank');
				setExporting(false);
			},
			(error: any) => {
				console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
				setExporting(false);
			},
			customerId
		);
	}

	useEffect(() => {
		fetchCustomerList();
	}, []);

	return (
		<StyledContainer>
			<PageHeader title={'Customers List'} />
			<Stack p={2} direction="row" justifyContent="space-between">
				<SearchBar
					style={{
						width: IsTabletWidth() ? '100%' : '312px',
						height: '36px',
					}}
					value={searchValue}
					onChange={handleSearch}
					isLoading={false}
				/>
				<ExportButtonStyles>
					<CustomButton
						title={exporting ? 'Exporting...' : 'Export All Customers'}
						color="primary"
						styles={{
							height: '36px',
							'@media (max-width: 478px)': {
								width: '100%',
							},
						}}
						handleClick={() => exportCustomerList(id)}
						disabled={exporting}
					/>
				</ExportButtonStyles>
			</Stack>
			<Box
				m={1}
				sx={{ width: isMobile ? 'calc(100vw - 10px)' : 'calc(100vw - 258px)', overflowX: 'auto' }}
			>
				<CustomDataGrid2
					rows={customerList}
					columns={columns}
					checkboxSelection={false}
					style={{
						transition: 'height 0.3s ease',
						maxHeight: 'calc(100vh - 250px)',
						minHeight: 'calc(100vh - 250px)',
					}}
					// showNoRowsFound={!orders?.length}
					onRowsSelectionHandler={onRowsSelectionHandler}
					handleRowClick={() => {}}
					// responsiveStyles={gridResponsiveStyles}
					// loading={loadingOrders}
					type="server"
					filterType="server"
				/>
				<Stack alignItems={'center'} mt={1}>
					<Pagination color="primary" count={totalPages} page={page} onChange={handlePageChange} />
				</Stack>
			</Box>
			<RenderCustomerOrders
				showAllCustomerOrders={showAllCustomerOrders}
				setShowAllCustomerOrders={setShowAllCustomerOrders}
				currentCustomer={currentCustomer}
				customerOrders={customerOrders}
				{...{ alert, setAlert, loadingOrders }}
			/>
			{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
		</StyledContainer>
	);
}

export default CustomerList;
